"use strict";
import { InterfaceElementName, InterfaceEventName } from "@uniswap/analytics-events";
import Loader from "components/Icons/LoadingSpinner";
import { DetectedBadge } from "components/WalletModal/shared";
import { useConnectorWithId } from "components/WalletModal/useOrderedConnections";
import { CONNECTOR_ICON_OVERRIDE_MAP, useRecentConnectorId } from "components/Web3Provider/constants";
import { walletTypeToAmplitudeWalletType } from "components/Web3Provider/walletConnect";
import { useConnect } from "hooks/useConnect";
import { useMemo } from "react";
import { Trans } from "react-i18next";
import { ThemedText } from "theme/components";
import { Flex, Text, useSporeColors } from "ui/src";
import { iconSizes } from "ui/src/theme";
import Badge, { BadgeVariant } from "components/badge/Badge";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import Trace from "features/telemetry/Trace";
import { isPlaywrightEnv } from "utilities/src/environment/env";
import { isIFramed } from "utils/isIFramed";
const RecentBadge = () => <Badge badgeVariant={BadgeVariant.SOFT} borderRadius={4} p={1} px={4}>
    <ThemedText.LabelMicro color="neutral2Temp">
      <Trans i18nKey="common.recent" />
    </ThemedText.LabelMicro>
  </Badge>;
function getIcon({
  connector,
  connectorId,
  themeColors
}) {
  const icon = CONNECTOR_ICON_OVERRIDE_MAP[connectorId] ?? connector?.icon;
  return <img
    src={icon}
    alt={connector?.name}
    style={{
      width: iconSizes.icon40,
      height: iconSizes.icon40,
      borderRadius: 8,
      border: `1px solid ${themeColors.surface3.val}`
    }}
  />;
}
function RightSideDetail({
  isPendingConnection,
  isRecent,
  detected
}) {
  if (isPendingConnection) {
    return <Loader />;
  } else if (isRecent) {
    return <RecentBadge />;
  } else if (detected) {
    return <DetectedBadge />;
  }
  return null;
}
export function Option({
  connectorId,
  detected,
  onPress
}) {
  const connection = useConnect();
  const connector = useConnectorWithId(connectorId);
  const isPendingConnection = connection.isPending && connection.variables?.connector === connector;
  const isRecent = connectorId === useRecentConnectorId();
  const themeColors = useSporeColors();
  const icon = getIcon({ connector, connectorId, themeColors });
  const text = connector?.name;
  const isDisabled = Boolean(connection?.isPending && !isIFramed());
  const { defaultChainId } = useEnabledChains();
  const handleConnect = useMemo(() => {
    return () => {
      if (onPress) {
        onPress();
        return;
      }
      if (!connector) return;
      if (isPlaywrightEnv()) {
        setTimeout(() => connection.connect({ connector, chainId: defaultChainId }), 1);
      } else {
        connection.connect({ connector, chainId: defaultChainId });
      }
    };
  }, [onPress, connector, connection, defaultChainId]);
  return <Flex
    backgroundColor="$surface2"
    row
    alignItems="center"
    width="100%"
    justifyContent="space-between"
    position="relative"
    px="$spacing12"
    py="$spacing18"
    cursor={isDisabled ? "auto" : "pointer"}
    hoverStyle={{ backgroundColor: isDisabled ? "$surface2" : "$surface1Hovered" }}
    opacity={isDisabled && !isPendingConnection ? 0.5 : 1}
    data-testid={`wallet-option-${connector?.type}`}
    onPress={handleConnect}
  >
      <Trace
    logPress
    eventOnTrigger={InterfaceEventName.WALLET_SELECTED}
    properties={{
      wallet_name: connector?.name ?? connectorId,
      wallet_type: walletTypeToAmplitudeWalletType(connector?.type ?? connectorId)
    }}
    element={InterfaceElementName.WALLET_TYPE_OPTION}
  >
        <Flex row alignItems="center" gap="$gap12">
          {icon}
          <Text variant="body2" py="$spacing8">
            {text}
          </Text>
        </Flex>
        <RightSideDetail isPendingConnection={isPendingConnection} isRecent={isRecent} detected={detected} />
      </Trace>
    </Flex>;
}
